import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import { Select, MenuItem, InputLabel, FormControl, CircularProgress, Box, TextField } from "@mui/material";
import LogoutButton from "layouts/logout";
import { useNavigate } from "react-router-dom";
import api from "core/interceptors/api";
import { disableBackNavigation } from "core/utils/storage";

// Helper function to format date
const formatDate = (datetimeString) => {
  if (!datetimeString) return "";
  const datePart = datetimeString.split(',')[0].trim();
  const parts = datePart.split('-');
  if (parts.length === 3) {
    return `${parts[0]}/${parts[1]}/${parts[2]}`;
  }
  return new Date(datetimeString).toLocaleDateString("en-GB");
};

// Helper function to format order items
const formatOrderItems = (items) => {
  try {
    const parsedItems = typeof items === "string" ? JSON.parse(items) : items;

    if (Array.isArray(parsedItems) && parsedItems.length > 0) {
      return parsedItems
        .map((item) => `${item.product_name} (Qty: ${item.quantity || 1})`)
        .join(", ");
    } else if (parsedItems && typeof parsedItems === 'object' && parsedItems.product_name) {
      return `${parsedItems.product_name} (Qty: ${parsedItems.quantity || 1})`;
    }
    return "N/A";
  } catch (error) {
    console.error("Error parsing order items:", error);
    return typeof items === "string" ? items : "Invalid Data Format";
  }
};

function VendorOrders() {
  const [vendorData, setVendorData] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState("all");
  const [vendors, setVendors] = useState([]);
  const email = localStorage.getItem("email");

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Search query state
  const [searchQuery, setSearchQuery] = useState("");

  // Memoize columns to prevent unnecessary re-renders of DataTable
  const columns = useMemo(() => [
    {
      Header: "S.No",
      accessor: "s_no",
      sortable: true,
      // IMPORTANT FIX: Calculate S.No based on current page and rowsPerPage
      Cell: ({ row: { index } }) => (page * rowsPerPage) + index + 1,
    },
    { Header: "Customer Name", accessor: "customer_name", sortable: true },
    { Header: "Order Date", accessor: "order_date", sortable: true },
    { Header: "Shipping Address", accessor: "shipping_address", sortable: true },
    { Header: "Billing Address", accessor: "billing_address", sortable: true },
    { Header: "Order Items", accessor: "order_items", sortable: true },
    { Header: "Quantity", accessor: "quantity", sortable: true },
    { Header: "Price", accessor: "price", sortable: true },
    { Header: "Total Amount", accessor: "total_amount", sortable: true },
    {
      Header: "Payment Status",
      accessor: "payment_status",
      sortable: true,
      Cell: ({ value }) => {
        let backgroundColor;
        let textColor = "white";
        let displayValue = value ? String(value).toUpperCase() : 'N/A';

        switch (displayValue) {
          case "PAID":
            backgroundColor = "#4CAF50"; // Green
            break;
          case "PENDING":
            backgroundColor = "#FFC107"; // Amber
            textColor = "#333333";
            break;
          case "FAILED":
            backgroundColor = "#F44336"; // Red
            break;
          case "REFUNDED":
            backgroundColor = "#2196F3"; // Blue
            break;
          default:
            backgroundColor = "#9E9E9E"; // Grey
            break;
        }

        return (
          <MDBox
            component="span"
            sx={{
              backgroundColor: backgroundColor,
              color: textColor,
              borderRadius: "8px",
              padding: "4px 10px",
              fontSize: "0.75rem",
              fontWeight: "bold",
              textTransform: "uppercase",
              display: "inline-block",
              minWidth: '70px',
              textAlign: 'center'
            }}
          >
            {displayValue}
          </MDBox>
        );
      },
    },
    {
      Header: "Order Status",
      accessor: "order_status",
      sortable: true,
      Cell: ({ value }) => {
        let backgroundColor;
        let textColor = "white";
        let displayValue = value ? String(value).toUpperCase() : 'N/A';

        switch (displayValue) {
          case "DELIVERED":
            backgroundColor = "#4CAF50"; // Green
            break;
          case "PROCESSING":
            backgroundColor = "#FF9800"; // Orange
            break;
          case "SHIPPED":
            backgroundColor = "#2196F3"; // Blue
            break;
          case "CANCELLED":
            backgroundColor = "#F44336"; // Red
            break;
          case "PENDING":
            backgroundColor = "#FFC107"; // Amber
            textColor = "#333333";
            break;
          default:
            backgroundColor = "#9E9E9E"; // Grey
            break;
        }

        return (
          <MDBox
            component="span"
            sx={{
              backgroundColor: backgroundColor,
              color: textColor,
              borderRadius: "8px",
              padding: "4px 10px",
              fontSize: "0.75rem",
              fontWeight: "bold",
              textTransform: "uppercase",
              display: "inline-block",
              minWidth: '90px',
              textAlign: 'center'
            }}
          >
            {displayValue}
          </MDBox>
        );
      },
    },
  ], [page, rowsPerPage]); // Add page and rowsPerPage to columns dependencies

  useEffect(() => {
    disableBackNavigation();
    fetchVendors();
    fetchOrders();
  }, []);

  const handleVendorChange = (event) => {
    const vendorId = event.target.value;
    setSelectedVendor(vendorId);
    setPage(0);
    fetchOrders(vendorId);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  useEffect(() => {
    const lowerCaseQuery = searchQuery.toLowerCase();
    const currentOrders = Array.isArray(vendorData) ? vendorData : [];

    const searchedData = currentOrders.filter(order => {
      return (
        order.customer_name?.toLowerCase().includes(lowerCaseQuery) ||
        formatDate(order.order_date).toLowerCase().includes(lowerCaseQuery) ||
        order.shipping_address?.toLowerCase().includes(lowerCaseQuery) ||
        order.billing_address?.toLowerCase().includes(lowerCaseQuery) ||
        formatOrderItems(order.order_items).toLowerCase().includes(lowerCaseQuery) ||
        order.quantity?.toString().toLowerCase().includes(lowerCaseQuery) ||
        order.price?.toString().toLowerCase().includes(lowerCaseQuery) ||
        order.total_amount?.toString().toLowerCase().includes(lowerCaseQuery) ||
        order.payment_status?.toLowerCase().includes(lowerCaseQuery) ||
        order.order_status?.toLowerCase().includes(lowerCaseQuery)
      );
    });
    setFilteredOrders(searchedData);

    if (page * rowsPerPage >= searchedData.length && searchedData.length > 0) {
      setPage(0);
    } else if (searchedData.length === 0 && page !== 0) {
      setPage(0);
    }
  }, [vendorData, searchQuery, page, rowsPerPage]);


  const fetchVendors = async () => {
    try {
      const res = await api.get(`/vendor_register/fetch_approved`);
      setVendors(res.data.vendors || []);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  const fetchOrders = async (vendor_id = 'all') => {
    setLoading(true);
    try {
      let emailToFetch = email;

      if (vendor_id !== 'all') {
        const selected = vendors.find(vendor => vendor.id === vendor_id);
        if (selected) {
          emailToFetch = selected.email;
        }
      }

      const res = await api.get(`/order_details/fetch`, {
        params: { email: emailToFetch },
      });
      setVendorData(res.data || []);
    } catch (error) {
      console.error("Error fetching Order Details:", error);
      setVendorData([]);
    } finally {
      setLoading(false);
    }
  };

  // The 'data' variable now just represents the currently filtered (but not yet paginated) orders.
  // The S.No calculation is done directly in the columns' Cell renderer.
  const data = useMemo(() => filteredOrders.map((order) => ({
    // s_no is handled in the Cell renderer
    customer_name: order.customer_name,
    order_date: formatDate(order.order_date),
    shipping_address: order.shipping_address,
    billing_address: order.billing_address,
    order_items: formatOrderItems(order.order_items),
    quantity: order.quantity,
    price: order.price,
    total_amount: order.total_amount,
    payment_status: order.payment_status,
    order_status: order.order_status,
  })), [filteredOrders]);

  const handleRowsPerPageChange = (event, newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Apply pagination to the 'data' (which is already filtered)
  const paginatedTableData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <DashboardLayout>
      <MDBox display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
        <LogoutButton
          onLogout={() => {
            navigate("/login");
          }}
        />
      </MDBox>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Order Details
                </MDTypography>
              </MDBox>

              <MDBox p={3} display="flex" justifyContent="space-between" alignItems="flex-end">
                <FormControl sx={{ minWidth: 200, flexGrow: 0 }}>
                  <InputLabel id="vendor-select-label">Select Vendor</InputLabel>
                  <Select
                    labelId="vendor-select-label"
                    value={selectedVendor}
                    onChange={handleVendorChange}
                    label="Select Vendor"
                    sx={{
                      height: 54,
                      paddingTop: "12px",
                      paddingBottom: "12px",
                    }}
                  >
                    <MenuItem value="all">
                      <em>All Orders (My Orders)</em>
                    </MenuItem>
                    {(Array.isArray(vendors) ? vendors : []).map((vendor) => (
                      <MenuItem key={vendor.id} value={vendor.id}>
                        {vendor.owner_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Search orders..."
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  sx={{ width: '300px', ml: 2 }}
                />
              </MDBox>

              <MDBox pt={loading ? 5 : 3} p={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress color="info" />
                    <MDTypography variant="h6" color="text" ml={2}>
                      Loading orders...
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns: columns, rows: paginatedTableData }}
                    isSorted={true}
                    entriesPerPage={{ defaultValue: rowsPerPage, entries: [5, 10, 15, 20, 25] }}
                    showTotalEntries={true}
                    canSearch={false}
                    noEndBorder
                    pagination={{
                      variant: "contained",
                      color: "dark",
                      page: page,
                      onPageChange: handlePageChange,
                      rowsPerPage: rowsPerPage,
                      onRowsPerPageChange: handleRowsPerPageChange,
                      count: filteredOrders.length,
                    }}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default VendorOrders;